import {
    FlatInjectionObj,
    InjectionsEntity,
} from '../../../../../../../../store/types/prescription';
import { INJECTION_COLUMN } from '../../../../../../../../utils/enums';
import { EditableCellV2 } from '../../../MedTableRenderer';
import AntAutoCompleteV2 from '../../../AntAutoComplete';
import { onFrequencySearch as onFrequencySearchWithProps } from '../../../onFrequencySearch';
import PROPERTIES from '../../../../properties';
import { useEffect, useMemo, useState } from 'react';
import { Popover } from 'antd';
import FrequencyDoseTimePopup from './frequencyColumnItems/FrequencyDoseTimePopup';
import AddItemChip from '../../../../../../../common/AddItemChip';
import { injectionRowHeaderMapping } from '../../../../../../../../constants';
import { INJECTIONS_TABLE_CONTAINER } from '../InjectionTable';
import { useDispatch, useSelector } from 'react-redux';
import { getInjectionElements } from '../../../../../../../../store/selectors/prescriptionPad';
import { upsertInjection } from '../../../../../../../../store/actions/prescriptionPad';
import moment from 'moment';

export type TSplitTiming = { timing: string | Date; quantity: string; id: number };

const InjectionFrequency = ({
    injection,
    columnKey,
    isDragMode,
}: {
    injection: FlatInjectionObj;
    columnKey: INJECTION_COLUMN;
    isDragMode: boolean;
}): JSX.Element => {
    const elements = useSelector(getInjectionElements);
    const dispatch = useDispatch();
    const initialSplitList = (injection?.frequency?.time_split || [])?.map((split, index) => {
        return {
            id: index,
            quantity: split.custom || '',
            timing: split.timing || '',
        };
    });
    const [showTimePopup, setShowTimePopup] = useState<boolean>(false);
    const [timingSplit, setTimingSplit] = useState<TSplitTiming[]>(initialSplitList);

    useEffect(() => {
        if (
            injection.frequency?.custom &&
            injection.frequency?.custom.includes('-') &&
            !(timingSplit && timingSplit.length > 0)
        ) {
            const frequencySplit = injection.frequency?.custom
                .split('-')
                .filter((value) => value != '0');
            setTimingSplit(
                frequencySplit.map((_, index) => ({ id: index, quantity: '', timing: '' })),
            );
        }
    }, [injection, timingSplit]);

    const filteredTimeSplit = useMemo(() => {
        const filteredSplit = timingSplit
            .filter((split) => split.timing)
            .map((split) => ({
                timing: split.timing,
                custom: split.quantity,
            }));
        return filteredSplit;
    }, [timingSplit]);

    const currentElement = injection.is_added_drug
        ? elements[injection.mainIndex]?.added_drug?.[injection.currentIndex]
        : elements[injection.mainIndex];

    const onFrequencySearch = (value: string): Promise<string[]> => {
        return onFrequencySearchWithProps(value, PROPERTIES.med.Frequency);
    };

    const onFrequencySelect = (option?: string | undefined) => {
        const { is_added_drug, is_tapering_dose, currentIndex, mainIndex } = injection;
        const clonedElement = elements[mainIndex];
        if (is_added_drug) {
            if (clonedElement.added_drug?.[currentIndex]) {
                clonedElement.added_drug[currentIndex].frequency = {
                    ...(clonedElement.added_drug[currentIndex].frequency || {}),
                    custom: option,
                };
            }
        } else if (is_tapering_dose) {
            if (clonedElement.tapering_dose?.[currentIndex]) {
                clonedElement.tapering_dose[currentIndex].frequency = {
                    ...(clonedElement.tapering_dose[currentIndex].frequency || {}),
                    custom: option,
                };
            }
        } else {
            if (clonedElement) {
                clonedElement.frequency = {
                    ...(clonedElement.frequency || {}),
                    custom: option,
                };
            }
        }
        if (option && !(timingSplit && timingSplit.length > 0)) {
            const frequencySplit = option.split('-').filter((value) => value != '0');
            setTimingSplit(
                frequencySplit.map((_, index) => ({ id: index, quantity: '', timing: '' })),
            );
        }
        dispatch(upsertInjection(clonedElement, mainIndex));
    };

    const onTimeSplitChange = (timingSplit: TSplitTiming[]) => {
        const filteredSplit = timingSplit
            .filter((split) => split.timing)
            .map((split) => ({
                timing: split.timing,
                custom: split.quantity,
            }));

        const { is_added_drug, is_tapering_dose, currentIndex, mainIndex } = injection;
        const clonedElement = elements[mainIndex];
        if (is_added_drug) {
            if (clonedElement.added_drug?.[currentIndex]) {
                clonedElement.added_drug[currentIndex].frequency = {
                    ...(clonedElement.added_drug[currentIndex].frequency || {}),
                    time_split: filteredSplit,
                };
            }
        } else if (is_tapering_dose) {
            if (clonedElement.tapering_dose?.[currentIndex]) {
                clonedElement.tapering_dose[currentIndex].frequency = {
                    ...(clonedElement.tapering_dose[currentIndex].frequency || {}),
                    time_split: filteredSplit,
                };
            }
        } else {
            if (clonedElement) {
                clonedElement.frequency = {
                    ...(clonedElement.frequency || {}),
                    time_split: filteredSplit,
                };
            }
        }
        dispatch(upsertInjection(clonedElement, mainIndex));
    };

    return isDragMode && (injection.is_added_drug || injection.is_tapering_dose) ? (
        <></>
    ) : (
        <>
            <div className="hover-parent-element flex space-y-4 overflow-y-scroll flex-col">
                <EditableCellV2
                    fieldName={columnKey}
                    defaultValue={injection?.frequency?.custom || ''}
                    label={injectionRowHeaderMapping[columnKey]}
                >
                    <AntAutoCompleteV2
                        autoFocus
                        onSearch={onFrequencySearch}
                        selectedValue={injection?.frequency?.custom || ''}
                        debounceDelay={200}
                        getLabelToCompare={(o) => o}
                        placeholder={'Frequency'}
                        customDropdownWidth={180}
                        onSelect={onFrequencySelect}
                        key={`Injection-${columnKey}`}
                        containerIdToAttachDropdown={INJECTIONS_TABLE_CONTAINER}
                    />
                </EditableCellV2>
            </div>
            <div className="p-6">
                {filteredTimeSplit?.map((split, index, splitArray) => (
                    <div className="text-11 font-400">
                        {moment(split.timing).format('hh:mm A')}{' '}
                        {split.custom ? `- ${split.custom}` : ''}
                        {index === splitArray.length - 1 ? '' : ','}
                    </div>
                ))}
            </div>
            <Popover
                placement="right"
                trigger="click"
                overlayInnerStyle={{
                    borderRadius: '16px',
                }}
                className="cursor-pointer"
                content={
                    <div className="w-360">
                        <FrequencyDoseTimePopup
                            onClose={() => setShowTimePopup(false)}
                            timeSplit={timingSplit}
                            onSplitChange={(timingSplit: TSplitTiming[]) => {
                                setTimingSplit(timingSplit);
                                onTimeSplitChange(timingSplit);
                            }}
                            element={currentElement as InjectionsEntity}
                        />
                    </div>
                }
                destroyTooltipOnHide={true}
                overlayClassName="popover-p-0"
                zIndex={1050}
                visible={showTimePopup}
                onVisibleChange={setShowTimePopup}
                getPopupContainer={() =>
                    document.getElementById(INJECTIONS_TABLE_CONTAINER) as HTMLElement
                }
            >
                <AddItemChip title="Add dose timing" onClick={() => setShowTimePopup(true)} />
            </Popover>
        </>
    );
};

export default InjectionFrequency;
